import React, { useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Eye, Edit2, ArrowUpRight } from "lucide-react";
import { DataTable, Avatar, Badge, Button, TableActionButton, TableStyles } from "../../../components/ui";
import { LEAD_TABLE_HEADERS as H, STATUS_VARIANT_MAP, STATUS_COLORS } from "@/constants/globalConstants";
import { TABLE_BACKGROUNDS } from "@/constants/tableBackgrounds";
import { getProjectPipelineDisplayStatus } from "../verve-projects-flow/constants/projectPipelineStages";

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";
const fmt = (v) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);

export const ProjectTableView = ({
  projects,
  onProjectClick,
  onStatusClick,
  onEditClick,
  onToggleDisable,
  onUpdateProject,
  updateStatus,
  onAddActivity,
  user,
  teamMembers = [],
  salesExecs = [],
  projectManagers = []
}) => {
  const [searchParams] = useSearchParams();
  const highlightedLeadId = searchParams.get("leadId");
  const navigate = useNavigate();

  const role = user?.role?.toLowerCase()?.trim()?.replace(/[-_\s]+/g, "-");
  const isSalesExec = role === 'sales-exec' || role === 'sale-exec' || role === 'sales-executive';
  const isSalesHead = role === 'sales-head' || role === 'sale-head';
  const isSuperAdmin = ['admin', 'owner', 'Verve-admin'].includes(role);

  const getStatusVariant = (status) => STATUS_VARIANT_MAP[status?.toLowerCase()] || STATUS_VARIANT_MAP.default;

  const columns = useMemo(() => {
    const cols = [
      {
        header: "Verve ID",
        key: "customId",
        minWidth: "170px",
        render: (l) => (
          <span className={`${TableStyles.idCell} cursor-pointer`} onClick={(e) => { e.stopPropagation(); if (onStatusClick) onStatusClick(l); }}>
            {l.customId || "—"}
          </span>
        )
      },
      {
        header: "Contact Details",
        key: "customerName",
        render: (l) => {
          const fullName = l.customerName || `${l.firstName || ""} ${l.lastName || ""}`.trim() || "No Name";
          return (
            <div className="flex items-center justify-start text-left gap-2 w-full max-w-[200px] cursor-pointer" onClick={(e) => { e.stopPropagation(); if (onStatusClick) onStatusClick(l); }}>
              <div className="flex flex-col min-w-0">
                <span className={TableStyles.primaryText}>
                  {fullName}
                </span>
                <span className={`${TableStyles.secondaryText} flex items-center gap-1 mt-0.5`}>
                  {l.phoneNumber || l.phone || "No Phone"}
                  {l.email && <span className="mx-1 opacity-50">|</span>}
                  {l.email && l.email.toLowerCase()}
                </span>
              </div>
            </div>
          );
        }
      },
      {
        header: "Source",
        key: "source",
        render: (l) => (
          <span className={`${TableStyles.badge} cursor-pointer`} onClick={(e) => { e.stopPropagation(); if (onStatusClick) onStatusClick(l); }}>
            {l.source || "Manual"}
          </span>
        )
      },
      {
        header: "Location",
        key: "location",
        render: (l) => (
          <div className="flex flex-col cursor-pointer" onClick={(e) => { e.stopPropagation(); if (onStatusClick) onStatusClick(l); }}>
            <span className={`${TableStyles.regularText} max-w-[120px] capitalize`}>
              {l.city || (l.location ? l.location.split(',')[0] : "—")}
            </span>
          </div>
        )
      }
    ];

    /*
    if (isSuperAdmin || isSalesHead || isSalesExec) {
      cols.push({
        header: "Telecaller",
        key: "teleInfo",
        render: (l) => {
          const teleInfo = l.telecallerId || l.telecaller;
          return teleInfo ? (
            <div className="flex flex-col">
              <div className="text-brand-dark text-[11px]">
                {teleInfo.name || `${teleInfo.firstName || ""} ${teleInfo.lastName || ""}`.trim() || "Unknown"}
              </div>
              <div className="text-[10px] text-slate-500">
                {l.updatedAt ? new Date(l.updatedAt).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : "—"}
              </div>
            </div>
          ) : (
            <span className="text-[11px] text-slate-400">Unassigned</span>
          );
        }
      });
    }
    */

    cols.push({
      header: "Last Note",
      key: "lastNote",
      className: "min-w-[250px]",
      render: (l) => {
        let displayNote = l.lastNote || l.notes || "No notes yet";
        if (displayNote && typeof displayNote === 'string' && displayNote.includes(' | Note: ')) {
          displayNote = displayNote.split(' | Note: ')[1].trim();
        }
        return (
          <span className={`${TableStyles.noteText} cursor-pointer`} onClick={(e) => { e.stopPropagation(); if (onStatusClick) onStatusClick(l); }}>
            {displayNote}
          </span>
        );
      }
    });

    if (isSuperAdmin || isSalesHead || isSalesExec) {
      cols.push({
        header: "Sales Exec",
        key: "assignedTo",
        render: (l) => {
          const execInfo = l.assignedTo;
          const execId = execInfo?._id || execInfo;
          const isValidSalesExec = salesExecs.some(u => String(u._id) === String(execId));
          
          return (execInfo && isValidSalesExec) ? (
            <div className="flex flex-col cursor-pointer" onClick={(e) => { e.stopPropagation(); if (onStatusClick) onStatusClick(l); }}>
              <div className="text-brand-dark text-[11px]">
                {execInfo.name || `${execInfo.firstName || ""} ${execInfo.lastName || ""}`.trim() || "Unknown"}
              </div>
            </div>
          ) : (
            <span className="text-[11px] text-slate-400 cursor-pointer" onClick={(e) => { e.stopPropagation(); if (onStatusClick) onStatusClick(l); }}>Unassigned</span>
          );
        }
      });

      // Assign PM Column
      cols.push({
        header: "Assign PM",
        key: "projectManager",
        render: (l) => {
          const pmArray = Array.isArray(l.projectManager) ? l.projectManager : (l.projectManager ? [l.projectManager] : []);
          const firstPM = pmArray[0];
          const currentPMId = firstPM?._id || firstPM || "";
          const pmName = firstPM?.name || `${firstPM?.firstName || ""} ${firstPM?.lastName || ""}`.trim() || null;
          const isPMAssigned = !!currentPMId;

          return (
            <div className="w-[180px]" onClick={e => e.stopPropagation()}>
              <select
                value={isPMAssigned ? currentPMId : ""}
                onClick={e => e.stopPropagation()}
                onChange={e => {
                  onUpdateProject(l._id, { projectManager: e.target.value ? [e.target.value] : [] });
                }}
                className={`w-full text-[11px] font-medium bg-white border border-slate-200 rounded-md px-3 py-1.5 outline-none focus:ring-2 focus:ring-brand-blue/20 transition-all cursor-pointer hover:border-brand-blue text-slate-700`}
              >
                <option value="">Unassigned</option>
                {projectManagers.map(u => (
                  <option key={u._id} value={u._id}>{u.name}</option>
                ))}
              </select>
            </div>
          );
        }
      });

      // Next Stage Dropdown — locked until PM is assigned
      /* cols.push({
        header: "Status",
        key: "nextStage",
        render: (l) => {
          const currentStatus = l.leadStatus || l.projectStatus || "cust_acceptance";
          const hasPM = !!(l.projectManager?._id || l.projectManager);
          const isLocked = l.isDisabled || ["project_started", "project_start"].includes(currentStatus) || !hasPM;
          const lockReason = !hasPM ? 'Assign a PM to enable stage progression' : null;

          const stageOptions = [
            { value: 'cust_acceptance', label: 'Client Accepted' },
            { value: 'design', label: 'Design' },
            { value: 'design_approval', label: 'Design Approval' },
            { value: 'final_quotation', label: 'Final Quotation' },
            { value: 'quotation_approval', label: 'Final Quotation Approval' },
            { value: 'documents', label: 'Sign Off' },
            { value: 'project_started', label: 'Started' },
          ];

          return (
            <div className="w-[165px]" onClick={e => e.stopPropagation()}>
              <select
                value={currentStatus}
                onChange={(e) => updateStatus(l, e.target.value)}
                title={lockReason || ''}
                className={`w-full text-[10px] uppercase text-brand-dark bg-white border rounded px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-brand-blue transition-colors ${
                  isLocked
                    ? 'border-slate-200 opacity-50 cursor-not-allowed bg-slate-50'
                    : 'border-slate-200 cursor-pointer hover:border-brand-blue'
                }`}
                disabled={isLocked}
              >
                {stageOptions.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>

            </div>
          );
        }
      }); */
//updated by Halimunnisa on 10-06-2026 - Added dynamic columns to ProjectTableView based on user role, including contact details, source, location, last note, assigned sales exec, and project manager assignment, along with a project status badge that reflects the current pipeline stage with appropriate color coding. Also included action buttons for viewing and editing projects, with conditional navigation to workflow tabs based on project status.
      // Project Status — reflects execution pipeline stage (Not started → Completed)
      cols.push({
        header: "Project Status",
        key: "projectStatus",
        render: (l) => {
          const label = getProjectPipelineDisplayStatus(l);
          const statusKey = label.toLowerCase().replace(/\s+/g, '_');

          return (
            <div 
              onClick={(e) => {
                e.stopPropagation();
                if (onStatusClick) onStatusClick(l);
              }}
              className="cursor-pointer inline-block"
              title="Click to view Project Flow"
            >
              <Badge
                variant={getStatusVariant(statusKey)}
                label={label}
                className="min-w-[90px] justify-center tracking-tighter text-[9px] px-2 py-0.5 rounded-full border shadow-sm whitespace-nowrap capitalize hover:opacity-80 transition-opacity"
              />
            </div>
          );
        }
      });
    }

    cols.push({
      header: "Created Date",
      key: "createdAt",
      minWidth: "100px",
      render: (l) => (
        <span className="text-[11px] text-slate-500 whitespace-nowrap">
          {l.createdAt ? new Date(l.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
        </span>
      )
    });

    cols.push({
      header: "Updated Date",
      key: "updatedAt",
      minWidth: "100px",
      render: (l) => (
        <span className="text-[11px] text-slate-500 whitespace-nowrap">
          {l.updatedAt ? new Date(l.updatedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : "—"}
        </span>
      )
    });

    cols.push({
      header: "Actions",
      key: "actions",
      render: (l) => {
        const activeStatus = l.projectStatus || "new";
        const validTabs = ['design', 'design_approval', 'final_quotation', 'quotation_approval', 'documents', 'project_started'];
        const isValidTab = validTabs.includes(activeStatus);
        const isDisabled = !isValidTab;

        return (
          <div className="flex items-center gap-2 justify-end">
            <TableActionButton
              icon={Eye}
              variant="primary"
              onClick={(e) => { e.stopPropagation(); onProjectClick(l); }}
              title="View Details"
            />
            <TableActionButton
              icon={Edit2}
              variant="orange"
              onClick={(e) => { e.stopPropagation(); onEditClick(l); }}
              title="Edit Project"
            />
            {/* User requested to hide this action button as it's not required right now 
            <TableActionButton
              icon={ArrowUpRight}
              variant="primary"
              disabled={isDisabled}
              onClick={(e) => {
                e.stopPropagation();
                if (!isDisabled) {
                  const leadId = l._id || l.id || "";
                  const idParam = leadId ? `&leadId=${leadId}` : "";
                  if (activeStatus === 'documents') {
                    navigate(`/documents?leadId=${leadId}`);
                  } else {
                    navigate(`/projects?tab=${activeStatus}${idParam}`);
                  }
                }
              }}
              title={isDisabled ? "No workflow tab available yet" : "Go to current stage tab"}
            />
            */}
          </div>
        );
      }
    });

    return cols;
  }, [isSalesExec, isSalesHead, isSuperAdmin, onAddActivity, onEditClick, onProjectClick, onToggleDisable, onUpdateProject, projectManagers, salesExecs, getStatusVariant]);

  return (
    <DataTable
      columns={columns}
      data={projects}
      backgroundImage={TABLE_BACKGROUNDS.projects}
      // Row click disabled so Verve ID, Contact Details, Source, Location, Last Note, Sales Exec do nothing
      // onRowClick={(row, idx) => onProjectClick(row)}
      getRowClassName={(row, idx) => {
        let classes = row?.isDisabled ? "opacity-50 bg-slate-50/50" : "";
        if (row._id === highlightedLeadId || row.customId === highlightedLeadId) {
          classes += " bg-amber-50/60 transition-colors duration-500";
        }
        return classes;
      }}
      className="border-none shadow-none rounded-none"
    />
  );
};
